import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllProvinces.graphql';
import { Query, QueryallProvincesArgs } from '../../__generated__/globalTypes';

export const useAllProvincesQuery = (options?: QueryHookOptions<Query, QueryallProvincesArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallProvincesArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllProvincesLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallProvincesArgs>
): QueryResult<Query, QueryallProvincesArgs> & {
  getAllProvinces: LazyQueryExecFunction<Query, QueryallProvincesArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllProvinces, result] = useLazyQuery<Query, QueryallProvincesArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllProvinces, ...result };
};

export const useAllProvincesPromiseQuery = () => {
  const client = useApolloClient();

  const getAllProvinces = useCallback(
    (options?: Omit<QueryOptions<QueryallProvincesArgs, Query>, 'query'>) =>
      client.query<Query, QueryallProvincesArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllProvinces };
};
// Suspense Query Hooks
export const useAllProvincesSuspenseQuery = (options?: SuspenseQueryHookOptions<Query, QueryallProvincesArgs>) => {
  const result = useSuspenseQuery<Query, QueryallProvincesArgs>(QUERY, {
    ...options
  });

  return result;
};
